import { NgClass, NgStyle } from '@angular/common';
import { SwiperOptions } from 'swiper/types';
import { Subscription } from 'rxjs';

import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, QueryList,
  ViewChildren, ViewChild, ViewEncapsulation, SimpleChanges, OnChanges,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  DeviceService, PositionEnum, SdBannerComponent, SdRichParagraphComponent,
  SdThemeComponent, SdThemeWrapperComponent, SizesEnum, ThemesEnum
} from '@seb/web-design-system/core';

import { SdSliderComponent } from '@seb/web-design-system/sliders';

import type { ITeaser } from './teaser.interface';

@Component({
  selector: 'sd-teaser',
  templateUrl: './teaser.component.html',
  styleUrls: ['./teaser.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdSliderComponent,
    NgClass,
    SdThemeWrapperComponent,
    SdBannerComponent,
    NgStyle,
    SdRichParagraphComponent
  ]
})
export class SdTeaserComponent extends SdThemeComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  isLoading = false;

  @Input()
  context = 'Teaser';

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 2;

  @Input()
  data: ITeaser[];

  @Input()
  autoplay = false;

  @Input()
  loop = false;

  @Input()
  mediasConfig: SwiperOptions = {
    loop: false,
    watchOverflow: true,
    pagination: {
      dynamicBullets: false,
      type: 'bullets',
      clickable: true
    },
    breakpoints: {
      0: {
        spaceBetween: 0,
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      768: {
        spaceBetween: 0,
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      1280: {
        spaceBetween: 0,
        slidesPerView: 1,
        slidesPerGroup: 1
      }
    }
  };

  @Input()
  twicPicsAutoSize = true;

  @Input()
  isLarge = false;

  @Input()
  hasAlphaHeader = false;

  @ViewChild('teaserSlider')
  teaserSlider: SdSliderComponent;

  @ViewChildren('mediaBanner')
  mediaBanner: QueryList<SdBannerComponent>;

  @ViewChildren('content')
  content: QueryList<ElementRef>;

  @Output()
  videoPlayed = new EventEmitter<{ status: string; title: string }>();

  @Output()
  videoEnded = new EventEmitter<{ status: string; title: string }>();

  @Output()
  clicked = new EventEmitter<any>();

  @Input() class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.contentClass,
      this.isLarge ? 'is-large' : '',
      this.isPlaying ? 'is-video-playing' : '',
      this.hasAlphaHeader ? 'has-alpha-header' : ''
    ]
      .filter(Boolean)
      .join(' ');
  };

  sizesEnum = SizesEnum;
  themesEnum = ThemesEnum;
  positionEnum = PositionEnum;
  contentClass: string;
  subscriptions = new Subscription();
  isDesktopLarge$: boolean;
  isMobile$: boolean;
  isPlaying = false;
  playVideo = false;
  currentSlideIndex = 0;
  timeoutCollector = [];
  numberOfVideo = 0;

  constructor(
    private deviceService: DeviceService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x) => {
        this.isMobile$ = x;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.deviceService.isDesktopLarge.subscribe((x) => {
        this.isDesktopLarge$ = x;
        this.cdRef.markForCheck();
      })
    );
  }

  ngOnChanges(changes?: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes['data']) {
      this.data?.forEach((item) => {
        if (item?.media?.isVideo) {
          this.numberOfVideo += 1;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.timeoutCollector.forEach((timeout) => clearTimeout(timeout));
  }

  onSlideChange(index: number): void {
    if (index !== this.currentSlideIndex) {
      this.currentSlideIndex = index || 0;
      this.updateVideoStates();
    }
  }

  onPlayStateChange(play: boolean, index?: number): void {
    this.isPlaying = play;
    this.playVideo = play;
    this.data[index].play = play;
    this.cdRef.markForCheck();
  }

  onPlay(index?: number): void {
    this.playVideo = true;
    this.data[index].play = true;
    this.cdRef.markForCheck();
  }

  onPause(index: number): void {
    if (this.data[index]) {
      this.data[index].play = false;
    }
    this.cdRef.markForCheck();
  }

  onVideoPlayed(title: string): void {
    this.videoPlayed.emit({
      status: 'play',
      title: this.context + ' - ' + title
    });
  }

  onVideoEnded(title: string): void {
    this.videoEnded.emit({
      status: 'ended',
      title: this.context + ' - ' + title
    });
  }

  onClick(event: any, teaserTitle?: string, position?: number): void {
    this.clicked.emit({ link: event.link, title: teaserTitle, position });
  }

  // TODO-EL: ATTENTION: l'event remonté par l'événement isVideoReady n'est pas un boolean =>
  // Regarder comment gérer ça directement dans le composant media-video
  // Pour l'instant, on force le isLoaded à true
  onIsVideoReady(_event: Event, index: number): void {
    this.data[index].isLoaded = true;
    if (this.checkAllVideoLoaded()) {
      this.updateVideoStates();
    }
  }

  private updateVideoStates(): void {
    this.data.forEach((item, i) => {
      if (i === this.currentSlideIndex && item.media?.isVideo && this.autoplay) {
        this.timeoutCollector.push(setTimeout(() => {
          this.onPlay(i);
        }, 1000));
      } else {
        this.onPause(i);
      }
    });
  }

  private checkAllVideoLoaded(): boolean {
    let loadedVideoNumber = 0;
    this.data.forEach((item) => {
      if (item?.isLoaded) {
        loadedVideoNumber += 1;
      }
    });
    return loadedVideoNumber === this.numberOfVideo;
  }
}
